import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { fetchComplaintDetail } from "../../../services/fsmService";
import { getQuotationById } from "../../../services/quotationService";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import {
  resolveBackendVersionNo,
  buildQuotationVersionHistory,
  extractNumericId,
  unwrapQuotationDetailPayload,
  hasQuotationDetailPayload,
  normalizeApiQuotation,
  hydrateQuotation,
  pickQuotationServiceRoleFields,
  mergeQuotationServiceRoleFields,
} from "./quotationUtils";
import { TableSkeleton, ErrorState } from "./QuotationComponents";
import { ManagerQuotationReview } from "./QuotationApprovalPage";
import { QuotationWorkspace } from "./QuotationWorkspace";


function QuotationDetailPage({ creationRoute = false }) {
  useTranslation();
  const { refId: routeRefId } = useParams();
  const refId = creationRoute ? "create" : routeRefId;
  const location = useLocation();
  const isMobileLayout = useIsMobileLayout();
  const [loading, setLoading] = useState(refId !== "create" && !location.state?.quotation);
  const [loadError, setLoadError] = useState("");
  const [remoteQuotation, setRemoteQuotation] = useState(null);
  const [remoteHistory, setRemoteHistory] = useState(null);

  const incomingQuotation = useMemo(
    () => (location.state?.quotation ? hydrateQuotation(location.state.quotation) : null),
    [location.state?.quotation]
  );
  const incomingHistory = useMemo(
    () => (location.state?.versionHistory?.map(hydrateQuotation) || null),
    [location.state?.versionHistory]
  );

  useEffect(() => {
    let active = true;

    if (refId === "create") {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    if (incomingQuotation) {
      setRemoteQuotation(incomingQuotation);
      setRemoteHistory(incomingHistory || [incomingQuotation]);
    }

    const numericId = extractNumericId(refId) || extractNumericId(incomingQuotation?.id);
    if (!numericId) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setLoadError("");

    (async () => {
      try {
        const response = await getQuotationById(numericId);
        if (!active) return;
        if (!hasQuotationDetailPayload(response?.data) && !hasQuotationDetailPayload(response)) {
          setRemoteQuotation(null);
          setRemoteHistory(null);
          setLoadError("Quotation details were not returned by the backend for this ID.");
          return;
        }
        const detailPayload =
          unwrapQuotationDetailPayload(response?.data) ||
          unwrapQuotationDetailPayload(response) ||
          {};
        const normalized = normalizeApiQuotation(detailPayload);
        // Preserve Service Executive / Manager from create navigation when GET omits them.
        let withServiceRoles =
          incomingQuotation &&
          String(normalized.type || "").toUpperCase() === "SERVICE"
            ? hydrateQuotation({
                ...normalized,
                ...mergeQuotationServiceRoleFields(normalized, incomingQuotation),
              })
            : normalized;

        // For saved SERVICE quotations, backfill IDs/names from complaint when GET is incomplete.
        const roles = pickQuotationServiceRoleFields(withServiceRoles);
        const complaintId = String(withServiceRoles.complaintId || "").trim();
        if (
          String(withServiceRoles.type || "").toUpperCase() === "SERVICE" &&
          complaintId &&
          (!roles.technicianId || !roles.managerId || !roles.technicianName || !roles.managerName)
        ) {
          try {
            const complaintDetail = await fetchComplaintDetail(complaintId);
            if (complaintDetail) {
              const raw =
                complaintDetail.raw && typeof complaintDetail.raw === "object"
                  ? complaintDetail.raw
                  : complaintDetail;
              withServiceRoles = hydrateQuotation({
                ...withServiceRoles,
                ...mergeQuotationServiceRoleFields(
                  withServiceRoles,
                  mergeQuotationServiceRoleFields(complaintDetail, raw)
                ),
              });
            }
          } catch {
            // Keep quotation GET mapping when complaint enrichment fails.
          }
        }

        if (!active) return;
        setRemoteQuotation(withServiceRoles);

        // Version history is loaded only when the Version History drawer opens —
        // do not call GET /saas/crm/quotations/get?searchTerm=… on quotation open.
        // Seed with the opened quotation only; do not pre-merge duplicates here.
        let history = [hydrateQuotation(withServiceRoles)];
        if (incomingHistory?.length) {
          const currentId = extractNumericId(withServiceRoles.id);
          const currentVersionNo = resolveBackendVersionNo(withServiceRoles);
          const currentRef = String(withServiceRoles.refId || "").trim().toUpperCase();

          const merged = [];
          const seenIds = new Set();
          const seenVersionKeys = new Set();

          const pushUnique = (entry) => {
            if (!entry) return;
            const id = extractNumericId(entry.id);
            const versionNo = resolveBackendVersionNo(entry);
            const refKey = String(entry.refId || "").trim().toUpperCase() || currentRef;
            const versionKey = versionNo != null ? `${refKey}::V${versionNo}` : "";

            if (id != null) {
              if (seenIds.has(id)) return;
              seenIds.add(id);
            } else if (versionKey && seenVersionKeys.has(versionKey)) {
              return;
            }
            if (versionKey) seenVersionKeys.add(versionKey);
            merged.push(entry);
          };

          incomingHistory.forEach((item) => {
            const itemId = extractNumericId(item.id);
            const itemVersionNo = resolveBackendVersionNo(item);
            const isSameAsCurrent =
              (currentId != null && itemId != null && currentId === itemId) ||
              (currentVersionNo != null &&
                itemVersionNo != null &&
                currentVersionNo === itemVersionNo &&
                String(item.refId || "").trim().toUpperCase() === currentRef);

            pushUnique(
              isSameAsCurrent
                ? hydrateQuotation({ ...withServiceRoles, superseded: item.superseded })
                : item
            );
          });

          const currentAlreadyPresent =
            (currentId != null && seenIds.has(currentId)) ||
            (currentVersionNo != null &&
              seenVersionKeys.has(`${currentRef}::V${currentVersionNo}`));
          if (!currentAlreadyPresent) {
            pushUnique(hydrateQuotation(withServiceRoles));
          }

          history = buildQuotationVersionHistory(merged, withServiceRoles.refId);
        }
        setRemoteHistory(history);
      } catch (error) {
        if (!active) return;
        setLoadError(error.message || "Failed to load quotation.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [refId]);



  if (!loading && !remoteQuotation && !incomingQuotation) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-[1320px]">
          <ErrorState message={loadError || "Quotation not found."} onRetry={() => window.location.reload()} />
        </div>
      </AppLayout>
    );
  }

  const baseQuotation = remoteQuotation || incomingQuotation;
  const baseHistory = remoteHistory || incomingHistory || [baseQuotation];
  const isPendingApproval =
    String(baseQuotation?.status || "").trim().toLowerCase() === "pending approval";
  const detailContent = isPendingApproval ? (
    <ManagerQuotationReview baseQuotation={baseQuotation} baseHistory={baseHistory} />
  ) : (
    <QuotationWorkspace baseQuotation={baseQuotation} baseHistory={baseHistory} />
  );
  // Mobile open-quotation / approval: pin footer (Next or Approve) below scroll (same as Create).
  const useMobileWorkspaceShell =
    isMobileLayout && !loading && Boolean(baseQuotation);

  return (
    <AppLayout
      className={
        useMobileWorkspaceShell ? "!flex !flex-col !overflow-hidden !py-4" : ""
      }
    >
      <div
        className={`mx-auto w-full max-w-[1320px] ${
          useMobileWorkspaceShell ? "flex min-h-0 flex-1 flex-col" : ""
        }`}
      >
        {loadError && <ErrorState message={loadError} onRetry={() => window.location.reload()} />}
        {loading ? (
          <ContentCard className="overflow-hidden rounded-[18px] border border-[#d3dceb] bg-white shadow-[0_12px_28px_-24px_rgba(30,51,131,0.22)]" bodyClassName="p-0">
            <TableSkeleton />
          </ContentCard>
        ) : (
          detailContent
        )}
      </div>
    </AppLayout>
  );
}

export {
  QuotationDetailPage
};
